import { inject, Injectable } from '@angular/core';

/**
 * libs
 */
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

/**
 * consts
 */
import { ExportTranslateMapper } from '../consts';

/**
 * interfaces
 */
import {
  ICsvExportRequest,
  IExcelExportRequest,
  IExporter,
  IExportRequest,
  IExportResult,
  IImageExportRequest,
  IPdfExportRequest,
} from '../interfaces';

/**
 * providers
 */
import { EXPORTERS } from '../provider/exporters.token';

/**
 * Facade service that delegates export requests to the proper strategy.
 */
@Injectable()
export class ExportFacadeService {

  /**
   * Exporters to use for export operations.
   */
  private readonly _exporters = inject(EXPORTERS);

  /**
   * Surfaces a failed export to the user — the interceptor that toasts HTTP
   * errors never sees a client-side one.
   */
  private readonly _toastr: ToastrService = inject(ToastrService);

  /**
   * Resolves the failure copy. Read eagerly per call rather than cached, so a
   * language switch is reflected without re-creating the facade.
   */
  private readonly _translate: TranslateService = inject(TranslateService);

  /**
   * Translate key map for the module's own copy.
   */
  private readonly _translateKeyMap = ExportTranslateMapper;

  /**
   * Export a table to a CSV file. Requires `rows` and `columns`.
   */
  public export<TRow = unknown>(request: ICsvExportRequest<TRow>): Promise<IExportResult>;

  /**
   * Export a table to an Excel workbook. Requires `rows` and `columns`.
   */
  public export<TRow = unknown>(request: IExcelExportRequest<TRow>): Promise<IExportResult>;

  /**
   * Export to a PDF, from either the request's sections (`contentType: 'table'`,
   * the default) or a rasterized DOM region (`contentType: 'element'`, which
   * requires `metadata.element`).
   */
  public export<TRow = unknown>(request: IPdfExportRequest<TRow>): Promise<IExportResult>;

  /**
   * Export a rendered region to an image. `metadata.element` is required;
   * `metadata.imageType` picks the encoding and defaults to `png`.
   */
  public export(request: IImageExportRequest): Promise<IExportResult>;

  /**
   * Export data to a file, with the format resolved at runtime.
   *
   * @param request - The export request.
   * @returns The outcome of the operation.
   *
   * @remarks
   * The default overload, and where a caller building one request literal for
   * several formats lands — `exportDataTable(format: ExportFormat)` being the
   * shape every table page uses. Metadata is the wide {@link IExportMetadata}
   * union there, so the compiler cannot check it against the format; the
   * strategies reject what they cannot use with a descriptive error.
   *
   * Because this signature is permissive and overloads resolve in order, a call
   * with a *literal* format that fails its own overload falls through to this
   * one instead of erroring. The strategies' runtime guards are what catch it.
   */
  public export<TRow = unknown>(request: IExportRequest<TRow>): Promise<IExportResult>;

  /**
   * Export data to a file.
   *
   * @param request - The export request.
   * @returns The outcome of the operation.
   *
   * @remarks
   * Never rejects. Callers written before the module went async do not await,
   * so a rejecting promise would turn every failure into an unhandled rejection
   * — the same silence this rewrite exists to remove. A failure is logged,
   * toasted, and reported through {@link IExportResult.ok} instead.
   */
  public async export<TRow = unknown>(request: IExportRequest<TRow>): Promise<IExportResult> {

    try {
      // Find the appropriate exporter for the request format
      const exporter = this._findExporter(request.format);

      // The `find` above is what establishes that this strategy handles this
      // request's format, and no signature can express that — the registry is
      // heterogeneous by design. Widening `TRow` is the only reason a cast is
      // needed at all: `IExportColumn<TRow>.transform` takes the row, so a
      // column list is contravariant in `TRow` and will not widen on its own.
      await exporter.export(request as IExportRequest);

      return { ok: true };
    }
    catch (error: unknown) {
      this._reportFailure(request, error);

      return { ok: false, error };
    }
  }

  /**
   * Find the appropriate exporter for the request format.
   * @param format - The format of the export request.
   * @returns The exporter for the request format.
   */
  private _findExporter(format: IExportRequest['format']): IExporter {
    // Find the exporter for the request format
    const exporter = this._exporters.find((item) => item.format === format);

    if (exporter == null) {
      throw new Error(`Exporter for format "${format}" is not registered.`);
    }

    return exporter;
  }

  /**
   * Logs a failed export and tells the user about it.
   *
   * @param request - The request that failed.
   * @param error - The thrown failure.
   */
  private _reportFailure(request: Pick<IExportRequest, 'format' | 'fileName'>, error: unknown): void {

    console.error(`[export] "${request.format}" export of "${request.fileName}" failed.`, error);

    this._toastr.error(
      this._translate.instant(this._translateKeyMap.FAILED_MESSAGE(), { format: request.format.toUpperCase() }),
      this._translate.instant(this._translateKeyMap.FAILED_TITLE()),
    );
  }
}
